import { useEffect } from 'react'
import { useGlobalStore } from '@highlight/ipc/react'
import { AUTO_CLEAR_INTERVAL_MS } from '../constants'
import { isMockAction, type StagedAction } from './use-staged-actions'

const AUTO_CLEAR_RUN_INTERVAL_MS = 8 * 60 * 60 * 1000
let isAutoClearRunning = false

/**
 * Periodically archives stale, never-invoked actions. Mounted exactly once (in
 * StagedActionsProvider, which passes in the data it already reads) so the
 * O(n) staleness scan + archive doesn't run per consumer. The module-level
 * guard prevents concurrent runs.
 */
export function useAutoClearStagedActions(stagedActions: {
  allActions: Array<StagedAction>
  bulkArchiveStagedActions: (ids: Array<string>) => Promise<void>
}): void {
  const { allActions, bulkArchiveStagedActions } = stagedActions
  const [autoClearInterval] = useGlobalStore<string>('settings', 'autoClearActions.interval', '72h')
  const [lastRunAt, setLastRunAt] = useGlobalStore<number | null>('settings', 'autoClearActions.lastRunAt', null)

  useEffect(() => {
    if (autoClearInterval === 'never' || !allActions.length) return
    if (lastRunAt !== null && Date.now() - lastRunAt < AUTO_CLEAR_RUN_INTERVAL_MS) return
    if (isAutoClearRunning) return

    const ms = AUTO_CLEAR_INTERVAL_MS[autoClearInterval]
    if (!ms) return

    const cutoff = Date.now() - ms
    const staleIds = allActions
      .filter((action) => {
        if (isMockAction(action.id)) return false
        if (action.lastInvokedAt) return false
        if (action.archivedAt != null) return false
        return new Date(action.createdAt).getTime() < cutoff
      })
      .map((action) => action.id)

    if (staleIds.length === 0) {
      setLastRunAt(Date.now())
      return
    }

    isAutoClearRunning = true
    bulkArchiveStagedActions(staleIds)
      .then(() => {
        setLastRunAt(Date.now())
      })
      .catch((error) => {
        console.error('[autoClear] bulk archive failed, will retry next window:', error)
      })
      .finally(() => {
        isAutoClearRunning = false
      })
  }, [allActions, autoClearInterval, lastRunAt, setLastRunAt, bulkArchiveStagedActions])
}
